import { queryOptions, useQuery } from '@tanstack/react-query';
import type {
  MorningCityConfigDto,
  MorningHolidayDto,
  MorningImageDto,
  MorningPhraseDto,
  MorningPreviewDto,
  MorningSettingsDto,
  MorningSlotCalendarResponse,
} from '@upme/contracts';
import { api } from '../../app/api-client';

export const morningKey = ['admin', 'morning'] as const;

export const morningSettingsOptions = () =>
  queryOptions({
    queryKey: [...morningKey, 'settings'],
    queryFn: () => api.get('admin/morning/settings').json<MorningSettingsDto>(),
  });

export const morningPhrasesOptions = (kind: 'GREETING' | 'CTA', cityFilter?: string) =>
  queryOptions({
    queryKey: [...morningKey, 'phrases', kind, cityFilter ?? 'all'],
    queryFn: () => {
      const params = new URLSearchParams({ kind });
      if (cityFilter) params.set('cityId', cityFilter);
      return api.get(`admin/morning/phrases?${params.toString()}`).json<MorningPhraseDto[]>();
    },
  });

export const morningImagesOptions = (cityFilter?: string) =>
  queryOptions({
    queryKey: [...morningKey, 'images', cityFilter ?? 'all'],
    queryFn: () => {
      const qs = cityFilter ? `?cityId=${encodeURIComponent(cityFilter)}` : '';
      return api.get(`admin/morning/images${qs}`).json<MorningImageDto[]>();
    },
  });

export const morningCityConfigOptions = (cityId: string) =>
  queryOptions({
    queryKey: [...morningKey, 'city-config', cityId],
    queryFn: () => api.get(`admin/morning/cities/${cityId}/config`).json<MorningCityConfigDto>(),
  });

export const morningHolidaysOptions = () =>
  queryOptions({
    queryKey: [...morningKey, 'holidays'],
    queryFn: () => api.get('admin/morning/holidays').json<MorningHolidayDto[]>(),
  });

export const morningPreviewOptions = (cityId: string, date?: string) =>
  queryOptions({
    queryKey: [...morningKey, 'preview', cityId, date ?? 'today'],
    queryFn: () => {
      const params = new URLSearchParams({ cityId });
      if (date) params.set('date', date);
      return api.get(`admin/morning/preview?${params.toString()}`).json<MorningPreviewDto>();
    },
    enabled: Boolean(cityId),
  });

export const morningSlotsOptions = (cityId: string, from: string, to: string) =>
  queryOptions({
    queryKey: [...morningKey, 'slots', cityId, from, to],
    queryFn: () => {
      const params = new URLSearchParams({ cityId, from, to });
      return api.get(`admin/morning/slots?${params.toString()}`).json<MorningSlotCalendarResponse>();
    },
    enabled: Boolean(cityId),
  });

export function useMorningSlots(cityId: string, from: string, to: string) {
  return useQuery(morningSlotsOptions(cityId, from, to));
}
